import { memo, useEffect, useMemo, useState } from 'react';
import { IOptions, RecursivePartial } from '@tsparticles/engine';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';

// Memoized Particles component to prevent re-renders
const MemoizedParticles = memo(function MemoizedParticles({
  options,
}: {
  options: RecursivePartial<IOptions>;
}) {
  return (
    <Particles
      id="tsparticles"
      options={options}
      className="absolute -bottom-[50px] -left-[50px] -right-[50px] -top-[50px] -z-10"
    />
  );
});

// Main component
function ParticleBackground() {
  const [initialized, setInitialized] = useState(false);

  // Memoize the particles options
  const options = useMemo(
    () =>
      ({
        autoPlay: true,
        backgroundMask: {
          enable: false,
        },
        clear: true,
        delay: 0,
        fullScreen: {
          enable: false,
        },
        detectRetina: true,
        fpsLimit: 120,
        interactivity: {
          detect_on: 'window',
          events: {
            onClick: {
              enable: false,
            },
            onHover: {
              enable: false,
            },
            resize: {
              enable: false,
            },
          },
        },
        particles: {
          number: {
            value: 600,
            density: {
              enable: true,
            },
          },
          color: {
            value: '#ffffff',
          },
          shape: {
            type: 'circle',
          },
          opacity: {
            value: 0.7,
          },
          size: {
            value: { min: 1, max: 2 },
          },
          links: {
            enable: true,
            distance: 200,
            color: '#ffffff',
            opacity: 0.2,
            width: 1,
          },
          move: {
            enable: true,
            speed: 0.8,
            direction: 'none' as const,
            random: true,
            straight: false,
            outModes: {
              default: 'bounce',
            },
          },
        },
        background: {
          color: '#93bed4',
        },
        pauseOnBlur: true,
        pauseOnOutsideViewport: true,
        responsive: [],
        smooth: false,
        style: {},
        themes: [],
        zLayers: 100,
        duration: 0,
        manualParticles: [],
      }) as RecursivePartial<IOptions>,
    [],
  ); // Empty dependency array since options don't change

  // Initialize the particles engine (should run once)
  useEffect(() => {
    const initEngine = async () => {
      await initParticlesEngine(async (engine) => {
        await loadSlim(engine);
      });
      setInitialized(true);
    };

    initEngine();
  }, []);

  // Only render the particles when initialized
  return initialized ? <MemoizedParticles options={options} /> : null;
}

// Export the memoized version
export default memo(ParticleBackground);
